import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Building2, ShoppingBag, Users, Hammer, Sparkles, Wind,
  CheckCircle, ArrowLeft, ClipboardCheck
} from "lucide-react";

import officeImg from "@/assets/service-office.png";
import retailImg from "@/assets/service-retail.png";
import strataImg from "@/assets/service-strata.png";
import constructionImg from "@/assets/service-construction.png";
import floorImg from "@/assets/service-floor.png";
import specialtyImg from "@/assets/service-specialty.png";

// Load static details mapped by serviceId
const servicesMap: Record<string, {
  title: string;
  subtitle: string;
  icon: any;
  image: string;
  desc: string;
  items: string[];
  metaTitle: string;
}> = {
  "commercial-office": {
    title: "Commercial Office Cleaning",
    subtitle: "Janitorial programs engineered for modern workspaces, corporate headquarters, and office buildings.",
    icon: Building2,
    image: officeImg,
    metaTitle: "Commercial Office Cleaning | Aureline",
    desc: "A clean office is essential for productivity, health, and team morale. Aureline offers daily, bi-weekly, or custom scheduled cleaning programs tailored specifically to your layout. Our supervised crews work with precision checklists to ensure that your lobbies, meeting rooms, and individual desks remain sanitary and pristine, backed by regular digital inspections sent directly to your management team.",
    items: [
      "Desk & workspace sanitization and wipe-downs",
      "Common area high-touch disinfection (handles, switches)",
      "Waste, recycling, and organic compost sorting",
      "Breakroom, kitchen, and appliance exterior cleaning",
      "Hard floor machine vacuuming and damp mopping",
      "Restroom detailing and supply replenishment"
    ]
  },
  "retail-cleaning": {
    title: "Retail & Showroom Cleaning",
    subtitle: "Spotless, welcoming presentation designed to match your brand's standards.",
    icon: ShoppingBag,
    image: retailImg,
    metaTitle: "Retail & Showroom Cleaning Services | Aureline",
    desc: "In retail, presentation is everything. Dust on displays or smudges on entry glass can impact your brand image. Aureline’s retail janitorial services focus on floor brilliance, glass clarity, and high-touch sanitization. We work around your store hours—operating early mornings or late nights—to ensure your retail floor is completely immaculate and ready to welcome guests every morning.",
    items: [
      "Storefront entrance glass and metal hardware polishing",
      "High-traffic floor vacuuming and machine buffing",
      "Point-of-sale terminal and checkout counter sanitization",
      "Customer fitting room detailing and disinfection",
      "Product shelving and luxury display case dusting",
      "Washroom maintenance and restocking programs"
    ]
  },
  "strata-common-areas": {
    title: "Strata & Common Area Cleaning",
    subtitle: "Lobbies, hallways, and common areas kept clean, presentable, and safe.",
    icon: Users,
    image: strataImg,
    metaTitle: "Strata & Common Area Cleaning | Aureline",
    desc: "Residential strata complexes require consistent, detail-oriented maintenance to preserve property values and keep residents comfortable. Aureline partners with strata councils and property managers across BC to service buildings of all sizes. From elevator detailing to vestibule sanitization, our cleaning plans are checklist-verified so councils have visual proof that common areas are managed responsibly.",
    items: [
      "Lobby floor scrubbing and elevator track vacuuming",
      "Hallway, foyer, and stairwell carpet vacuuming",
      "Entrance door glass cleaning and handle polishing",
      "Trash/recycle station organization and cleanup",
      "Amenity room (gyms, lounges, patios) sanitizing",
      "Weekly checklist updates for strata boards"
    ]
  },
  "post-construction": {
    title: "Post-Construction Detail Cleaning",
    subtitle: "Drywall dust mitigation, adhesive removal, and detailed handover cleaning.",
    icon: Hammer,
    image: constructionImg,
    metaTitle: "Post-Construction Cleaning Services | Aureline",
    desc: "Renovations and new construction leave behind fine particulate dust that settles in every corner. Standard cleaning cannot handle the detailing required for a successful building turnover. Aureline’s post-construction crews are equipped with heavy-duty HEPA vacuums, specialized tools, and detailed checklists to clean from ceiling to floor. We deliver a spotless, move-in-ready space for your clients, tenants, or buyers.",
    items: [
      "Fine drywall dust HEPA-vacuuming and wipe-down",
      "Adhesive, paint overspray, and sticker removal from glass",
      "Detailed window frame, sill, and track cleaning",
      "Interior/exterior cabinet, shelf, and drawer cleaning",
      "Light fixture, electrical panel, and outlet wipe-downs",
      "Full appliance detailing and protective film extraction"
    ]
  },
  "floor-surface-care": {
    title: "Floor & Surface Care Services",
    subtitle: "Specialized hard-floor scrub, seal, wax, and restoration treatments.",
    icon: Sparkles,
    image: floorImg,
    metaTitle: "Floor & Surface Care - Strip, Wax & Scrub | Aureline",
    desc: "Floors represent a significant asset in any building. Without proper maintenance, high-traffic corridors lose their luster and accumulate embedded grime. Aureline’s specialty floor division handles restoration and ongoing maintenance programs for all floor surfaces. We utilize commercial-grade floor scrubbers, high-speed burnishers, and premium seals to extend floor lifespan and keep surfaces reflecting a bright, clean sheen.",
    items: [
      "Vinyl Composite Tile (VCT) strip, seal, and high-gloss wax",
      "Ceramic tile and grout deep machine scrubbing",
      "Concrete cleaning, sealing, and protective coating",
      "High-traffic carpet hot water extraction & spot removal",
      "Hardwood floor polishing and screen-and-coat finishes",
      "Anti-slip floor coating applications"
    ]
  },
  "residential": {
    title: "Residential Cleaning",
    subtitle: "Premium home deep cleaning, move-in/move-out details, and sanitization.",
    icon: Wind,
    image: specialtyImg,
    metaTitle: "Residential Cleaning Services | Aureline",
    desc: "Your home is your sanctuary. Aureline brings the same precision, digital tracking, and supervision from our commercial division directly to premium residential properties. Whether you need a comprehensive deep clean, move-in/move-out detailing, or rotating estate maintenance, our vetted crews follow meticulous checklists and deliver quality inspection reports for ultimate peace of mind.",
    items: [
      "Whole-home deep sanitization and dusting",
      "Detailed kitchen and washroom disinfection",
      "High-touch surface sanitizing (handles, switches)",
      "Window sills, frames, and baseboard detailing",
      "Hardwood floor care and carpet vacuuming",
      "Post-occupancy and turnover deep cleaning"
    ]
  }
};

export const Route = createFileRoute("/services/$serviceId")({
  loader: ({ params }) => {
    const service = servicesMap[params.serviceId];
    if (!service) {
      throw notFound();
    }
    return service;
  },
  component: ServiceDetail,
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData.metaTitle },
      { name: "description", content: loaderData.subtitle },
    ],
  }),
});

function ServiceDetail() {
  const service = Route.useLoaderData();
  const Icon = service.icon;

  return (
    <Layout>
      {/* BACK NAVIGATION BAR */}
      <section className="bg-cream py-4 border-b border-border/40">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-navy hover:text-navy-soft font-medium transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all services
          </Link>
        </div>
      </section>

      {/* HERO SECTION */}
      <section className="relative py-20 bg-gradient-to-b from-secondary/35 via-secondary/10 to-background border-b border-border/30">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 xl:gap-24 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-navy eyebrow animate-fade-up">
                <Icon className="w-4 h-4 text-navy-soft animate-pulse" /> Detailed Service Plan
              </div>
              <h1 className="mt-6 font-display text-4xl md:text-6xl text-navy leading-[1.25] tracking-tight animate-fade-up delay-100">
                {service.title}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl animate-fade-up delay-200">
                {service.subtitle}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DETAILED CONTENT & IMAGE */}
      <section className="py-24">
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 xl:gap-24 items-start">
            {/* Left Content */}
            <div className="lg:col-span-6 space-y-8">
              <div>
                <h2 className="font-display text-3xl md:text-4xl text-navy leading-snug tracking-tight">
                  Professional service scaled to your footprint.
                </h2>
                <p className="mt-6 text-muted-foreground xl:text-lg leading-relaxed">
                  {service.desc}
                </p>
              </div>

              <div className="border-t border-border/60 pt-8">
                <h3 className="font-display text-xl text-navy font-semibold mb-6 uppercase tracking-wider text-xs">
                  Included Service Checklist Items:
                </h3>
                <ul className="grid sm:grid-cols-2 gap-4">
                  {service.items.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm xl:text-base text-muted-foreground">
                      <CheckCircle className="w-5 h-5 text-mint-ink flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Image Container */}
            <div className="lg:col-span-6 hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-elevated animate-scale-in">
                <img
                  src={service.image}
                  alt={`${service.title} in progress`}
                  width={1280}
                  height={1280}
                  className="w-full h-[400px] xl:h-[550px] 2xl:h-[650px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 gradient-navy-blue text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 stripe-accent-fade" aria-hidden />
        <div className="max-w-[92%] 2xl:max-w-[1720px] mx-auto px-6 relative z-10 text-center">
          <div className="max-w-3xl mx-auto">
            <ClipboardCheck className="w-12 h-12 text-mint mx-auto" />
            <h2 className="mt-6 font-display text-4xl md:text-5xl tracking-tight">
              Request a verified program for {service.title}
            </h2>
            <p className="mt-6 text-primary-foreground/80 xl:text-lg leading-relaxed">
              Every shift is backed by our digital checklist, quality reporting, and supervisory oversight. 
              Connect with us to design a plan that matches your building's precise needs.
            </p>
            <div className="mt-10 flex justify-center">
              <Link to="/" hash="contact">
                <Button 
                  className="rounded-full h-12 px-8 bg-mint text-mint-ink hover:bg-mint/90 text-base font-semibold"
                  style={{ textShadow: "0 1px 2px rgba(0, 0, 0, 0.35)" }}
                >
                  Get a custom quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
